import React, { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ChevronLeft,
  ChevronRight,
  Users,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  Upload,
  X,
  GripVertical
} from "lucide-react";

import { useForm } from "react-hook-form";
import { z } from "zod";
import PageHeader from "../components/common/PageHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import useToast from "../hooks/useToast";
import { uploadFileToS3 } from "../services/uploadService";
import {
  createTrusty,
  deleteTrusty,
  getTrusties,
  updateTrusty,
  updateTrustyOrder
} from "../services/trustyService";


const trustySchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  designation: z.string().trim().min(1, "Designation is required"),
  profile_image: z.string().trim().optional()
});

const defaultValues = {
  name: "",
  designation: "",
  profile_image: ""
};

function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function getVisiblePageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "...", totalPages];
  }

  if (currentPage >= totalPages - 3) {
    return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
}

export default function Trusty() {
  const [trusties, setTrusties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isDeletingTrusty, setIsDeletingTrusty] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTrustyId, setEditingTrustyId] = useState(null);
  const [trustyPendingDelete, setTrustyPendingDelete] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 5,
    total: 0,
    totalPages: 1
  });
  const [draggingIndex, setDraggingIndex] = useState(null);

  const fileInputRef = useRef(null);

  const { showErrorToast, showSuccessToast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues,
    resolver: zodResolver(trustySchema)
  });

  const profileImage = watch("profile_image");
  const previewName = watch("name");

  const refreshListing = (targetPage = pagination.page, targetLimit = pagination.limit) => {
    if (targetPage !== pagination.page || targetLimit !== pagination.limit) {
      setPagination((current) => ({
        ...current,
        page: targetPage,
        limit: targetLimit
      }));
      return;
    }

    loadTrusties(targetPage, targetLimit);
  };

  async function loadTrusties(pageArg = pagination.page, limitArg = pagination.limit) {
    setIsLoading(true);

    const response = await getTrusties({
      page: pageArg,
      limit: limitArg
    });

    if (response?.success) {
      setTrusties(response.data.trusty || []);
      setPagination({
        page: response.data.pagination?.page || pageArg,
        limit: response.data.pagination?.limit || limitArg,
        total: response.data.pagination?.total || 0,
        totalPages: response.data.pagination?.totalPages || 1
      });
    } else {
      showErrorToast(response?.message || "Unable to load Trustee records");
    }

    setIsLoading(false);
  }

  useEffect(() => {
    loadTrusties(pagination.page, pagination.limit);
  }, [pagination.page, pagination.limit]);

  const openCreateModal = () => {
    reset(defaultValues);
    setEditingTrustyId(null);
    setIsDragActive(false);
    setIsUploadingImage(false);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    reset(defaultValues);
    setEditingTrustyId(null);
    setIsDragActive(false);
    setIsUploadingImage(false);
    setIsModalOpen(false);
  };

  const openDeleteModal = (trusty) => {
    setTrustyPendingDelete(trusty);
  };

  const closeDeleteModal = () => {
    if (isDeletingTrusty) {
      return;
    }

    setTrustyPendingDelete(null);
  };

  const handleFileRead = (file) => {
    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      showErrorToast("Please upload an image file");
      return;
    }

    setIsUploadingImage(true);

    uploadFileToS3(file, "trusty").then((response) => {
      if (!response?.success) {
        showErrorToast(response?.message || "Unable to upload image");
        setIsUploadingImage(false);
        return;
      }

      setValue("profile_image", response.data.file.url, {
        shouldDirty: true,
        shouldValidate: true
      });
      showSuccessToast("Image uploaded successfully");
      setIsUploadingImage(false);
    });
  };

  const handleFileSelect = (event) => {
    handleFileRead(event.target.files?.[0]);
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragActive(false);
    handleFileRead(event.dataTransfer.files?.[0]);
  };

  const handleEdit = (trusty) => {
    setEditingTrustyId(trusty.id);
    reset({
      name: trusty.name,
      designation: trusty.designation,
      profile_image: trusty.profile_image || ""
    });
    setIsDragActive(false);
    setIsUploadingImage(false);
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!trustyPendingDelete) {
      return;
    }

    setIsDeletingTrusty(true);

    const response = await deleteTrusty(trustyPendingDelete.id);

    setIsDeletingTrusty(false);

    if (!response?.success) {
      showErrorToast(response?.message || "Unable to delete Trustee");
      return;
    }

    const nextPage = trusties.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page;
    showSuccessToast(response.message || "Trustee deleted successfully");
    closeDeleteModal();
    refreshListing(nextPage, pagination.limit);
  };

  const onSubmit = async (values) => {
    const payload = {
      ...values,
      profile_image: values.profile_image || ""
    };

    const response = editingTrustyId
      ? await updateTrusty(editingTrustyId, payload)
      : await createTrusty(payload);

    if (!response?.success) {
      showErrorToast(response?.message || "Unable to save Trustee");
      return;
    }

    showSuccessToast(response.message || "Trustee saved successfully");
    closeModal();

    if (editingTrustyId) {
      refreshListing(pagination.page, pagination.limit);
      return;
    }

    refreshListing(1, pagination.limit);
  };

  const handleRowDragStart = (e, index) => {
    setDraggingIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleRowDragOver = (e) => {
    e.preventDefault();
  };

  const handleRowDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (draggingIndex === null || draggingIndex === dropIndex) return;

    const items = [...trusties];
    const draggedItem = items[draggingIndex];
    items.splice(draggingIndex, 1);
    items.splice(dropIndex, 0, draggedItem);

    setTrusties(items);
    setDraggingIndex(null);

    const orderData = items.map((item, index) => ({
      id: item.id,
      sort_order: index + (pagination.page - 1) * pagination.limit
    }));

    const response = await updateTrustyOrder(orderData);
    if (!response?.success) {
      showErrorToast(response?.message || "Unable to save order");
      loadTrusties(pagination.page, pagination.limit);
    } else {
      showSuccessToast("Order updated");
    }
  };

  const handleRowDragEnd = () => {
    setDraggingIndex(null);
  };

  const startItem = pagination.total ? (pagination.page - 1) * pagination.limit + 1 : 0;

  const endItem = pagination.total ? Math.min(pagination.page * pagination.limit, pagination.total) : 0;
  const pageNumbers = getVisiblePageNumbers(pagination.page, pagination.totalPages);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Management"
        title="Trustees"
        description="Manage Trustee records from a paginated listing page with modal-based create and edit forms."
        badge={`${pagination.total} Profiles`}
      />

      <Card className="border-white/90 bg-white/84">
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Trustees Listing</CardTitle>
            <p className="mt-2 text-sm text-panel-muted">
              View Trustee records, paginate from the server, and use modal actions for add and edit.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" onClick={openCreateModal}>
              <Plus className="mr-2 h-4 w-4" />
              Add Trustee
            </Button>

          </div>

        </CardHeader>
        <CardContent className="space-y-5">
          {isLoading ? (
            <div className="rounded-3xl border border-slate-100 bg-slate-50/80 p-6 text-sm text-panel-muted">
              Loading Trustee records...
            </div>
          ) : trusties.length ? (
            <>
              <div className="overflow-hidden rounded-3xl border border-slate-100">
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead className="bg-slate-50/90">
                      <tr className="text-left">
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted w-10">Drag</th>

                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Profile</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Name</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Designation</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Actions</th>
                      </tr>
                    </thead>

                    <tbody className="bg-white">
                      {trusties.map((trusty, index) => (
                        <tr 
                          key={trusty.id} 
                          className={`border-t border-slate-100 align-top cursor-move ${draggingIndex === index ? 'opacity-50' : ''}`}
                          draggable={true}
                          onDragStart={(e) => handleRowDragStart(e, index)}
                          onDragOver={handleRowDragOver}
                          onDrop={(e) => handleRowDrop(e, index)}
                          onDragEnd={handleRowDragEnd}
                        >

                          <td className="px-5 py-4 align-middle">
                            <div className="flex items-center justify-center p-2 rounded-lg hover:bg-slate-100">
                              <GripVertical className="h-5 w-5 text-panel-muted" />
                            </div>
                          </td>

                          <td className="px-5 py-4">
                            {trusty.profile_image ? (
                              <img
                                src={trusty.profile_image}
                                alt={trusty.name}
                                className="h-14 w-14 rounded-2xl object-cover"
                              />
                            ) : (
                              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-sm font-bold text-white">
                                {getInitials(trusty.name)}
                              </div>
                            )}
                          </td>
                          <td className="px-5 py-4 text-sm font-semibold text-panel-ink">{trusty.name}</td>
                          <td className="px-5 py-4 text-sm text-primary">{trusty.designation}</td>
                          <td className="px-5 py-4">
                            <div className="flex gap-2">
                              <Button type="button" size="sm" variant="outline" onClick={() => handleEdit(trusty)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                              </Button>
                              <Button type="button" size="sm" variant="ghost" className="hover:bg-rose-50 hover:text-rose-600 text-panel-muted" onClick={() => openDeleteModal(trusty)}>
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </Button>
                            </div>
                          </td>

                        </tr>
                      ))}

                    </tbody>
                  </table>
                </div>
              </div>

               <div className="flex flex-col gap-4 rounded-3xl border border-slate-100 bg-slate-50/70 px-4 py-4 md:flex-row md:items-center md:justify-between">

                  <p className="text-sm text-panel-muted">
                    Showing <span className="font-semibold text-panel-ink">{startItem}</span> to{" "}
                    <span className="font-semibold text-panel-ink">{endItem}</span> of{" "}
                    <span className="font-semibold text-panel-ink">{pagination.total}</span> Trustee records
                  </p>

                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 text-sm text-panel-muted">
                      Rows
                      <select
                        className="h-10 rounded-2xl border border-slate-200 bg-white px-3 text-sm text-panel-ink outline-none"
                        value={pagination.limit}
                        onChange={(event) =>
                          setPagination((current) => ({
                            ...current,
                            page: 1,
                            limit: Number(event.target.value)
                          }))
                        }
                      >
                        {[5, 10, 20].map((value) => (
                          <option key={value} value={value}>
                            {value}
                          </option>
                        ))}
                      </select>
                    </label>

                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={pagination.page <= 1}
                        onClick={() =>
                          setPagination((current) => ({
                            ...current,
                            page: current.page - 1
                          }))
                        }
                      >
                        <ChevronLeft className="mr-1 h-4 w-4" />
                        Prev
                      </Button>
                      <div className="flex flex-wrap items-center gap-2">
                        {pageNumbers.map((item, index) =>
                          item === "..." ? (
                            <span key={`ellipsis-${index}`} className="px-1 text-sm font-medium text-panel-muted">
                              ...
                            </span>
                          ) : (
                            <Button
                              key={item}
                              type="button"
                              size="sm"
                              variant={pagination.page === item ? "default" : "outline"}
                              onClick={() =>
                                setPagination((current) => ({
                                  ...current,
                                  page: item
                                }))
                              }
                            >
                              {item}
                            </Button>
                          )
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={pagination.page >= pagination.totalPages}
                        onClick={() =>
                          setPagination((current) => ({
                            ...current,
                            page: current.page + 1
                          }))
                        }
                      >
                        Next
                        <ChevronRight className="ml-1 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>


            </>
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50/80 p-8 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Sparkles className="h-6 w-6" />
              </div>
              <p className="mt-4 text-lg font-semibold text-panel-ink">No Trustee records added yet</p>
              <p className="mt-2 text-sm text-panel-muted">
                Add your first Trustee record with name, designation, and profile image.
              </p>
              <Button type="button" className="mt-5" onClick={openCreateModal}>
                <Plus className="mr-2 h-4 w-4" />
                Add Trustee
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {isModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4">
          <div className="max-h-[90dvh] w-full max-w-4xl overflow-y-auto rounded-[32px] border border-white/80 bg-white shadow-[0_24px_80px_rgba(44,33,111,0.22)]">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white/95 px-6 py-5 backdrop-blur">
              <div>
                <h2 className="text-2xl font-bold text-panel-ink">
                  {editingTrustyId ? "Edit Trustee" : "Add Trustee"}
                </h2>
                <p className="mt-1 text-sm text-panel-muted">
                  Name and designation are mandatory. Profile image uses drag-and-drop upload.
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex h-11 w-11 items-center justify-center rounded-2xl text-panel-muted transition-colors hover:bg-slate-100 hover:text-panel-ink"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid gap-5 p-6 xl:grid-cols-[340px_1fr]">
              <div className="space-y-4">
                <div
                  className={`rounded-3xl border-2 border-dashed p-5 transition-colors ${
                    isDragActive ? "border-primary bg-primary/5" : "border-slate-200 bg-slate-50/70"
                  }`}
                  onDragEnter={(event) => {
                    event.preventDefault();
                    setIsDragActive(true);
                  }}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setIsDragActive(true);
                  }}
                  onDragLeave={(event) => {
                    event.preventDefault();
                    setIsDragActive(false);
                  }}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileSelect}
                  />

                  <div className="flex flex-col items-center text-center">
                    {profileImage ? (
                      <img
                        src={profileImage}
                        alt={previewName || "Trustee preview"}
                        className="h-36 w-36 rounded-[28px] object-cover shadow-sm"
                      />
                    ) : (
                      <div className="flex h-36 w-36 items-center justify-center rounded-[28px] bg-primary text-3xl font-bold text-white shadow-sm">
                        {getInitials(previewName || "TR")}
                      </div>
                    )}

                    <p className="mt-4 text-base font-semibold text-panel-ink">Profile Image Upload</p>
                    <p className="mt-2 max-w-xs text-sm leading-6 text-panel-muted">
                      Drag and drop an image here, or click the button below to upload.
                    </p>

                    <div className="mt-4 flex flex-wrap justify-center gap-2">
                       <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
                        <Upload className="mr-2 h-4 w-4" />
                        {isUploadingImage ? "Uploading..." : "Upload Image"}
                      </Button>
                      {profileImage ? (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() =>
                            setValue("profile_image", "", {
                              shouldDirty: true,
                              shouldValidate: true
                            })
                          }
                          disabled={isUploadingImage}
                        >
                          <X className="mr-2 h-4 w-4" />
                          Remove
                        </Button>
                      ) : null}
                    </div>
                    {isUploadingImage ? (
                      <p className="mt-3 text-sm font-medium text-primary">Uploading to AWS S3...</p>
                    ) : null}
                  </div>
                </div>
              </div>

              <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
                <input type="hidden" {...register("profile_image")} />

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <FieldLabel>Name</FieldLabel>
                    <Input placeholder="Enter Trustee name" {...register("name")} />
                    {errors.name ? <FieldError>{errors.name.message}</FieldError> : null}
                  </div>

                  <div className="space-y-2">
                    <FieldLabel>Designation</FieldLabel>
                    <Input placeholder="Enter designation" {...register("designation")} />
                    {errors.designation ? <FieldError>{errors.designation.message}</FieldError> : null}
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <Button type="submit" disabled={isSubmitting || isUploadingImage}>
                    {isSubmitting ? "Saving..." : editingTrustyId ? "Update Trustee" : "Save Trustee"}
                  </Button>
                  <Button type="button" variant="outline" onClick={closeModal}>
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}

      {trustyPendingDelete ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-md rounded-[32px] border border-white/80 bg-white p-6 shadow-[0_24px_80px_rgba(44,33,111,0.22)]">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-50 text-rose-600">
              <Trash2 className="h-5 w-5" />
            </div>
            <h2 className="mt-5 text-2xl font-bold text-panel-ink">Delete Trustee</h2>
            <p className="mt-3 text-sm leading-6 text-panel-muted">
              Are you sure you want to delete <span className="font-semibold text-panel-ink">{trustyPendingDelete.name}</span>?
              This action will remove the record from the listing.
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <Button type="button" variant="destructive" onClick={handleDelete} disabled={isDeletingTrusty}>
                {isDeletingTrusty ? "Deleting..." : "Delete Trustee"}
              </Button>
              <Button type="button" variant="outline" onClick={closeDeleteModal} disabled={isDeletingTrusty}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
